import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { api } from "./api";
import type { Catalog, Profile, Session, Visibility } from "./api";
import { Badge, Icon, PageTitle, errorMessage } from "./components";
import { PreferencesPanel, NotificationsPanel } from "./ProfileSettings";

export function AuthDialog({
  onClose,
  onSuccess,
  initialMode = "signup",
}: {
  onClose: () => void;
  onSuccess: (session: Session) => void;
  initialMode?: "signup" | "login";
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [mode, setMode] = useState<"signup" | "login">(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = awaitingConfirmation
        ? await api.confirm({ email, token: confirmationCode.trim() })
        : await (mode === "signup"
            ? api.signup({ email, password, display_name: name })
            : api.login({ email, password }));
      if (result.person) onSuccess(result);
      else if (result.auth?.status === "awaiting_confirmation") {
        setAwaitingConfirmation(true);
        setPassword("");
        setConfirmationMessage(
          result.auth.message ||
            "Ouvrez le lien reçu par e-mail pour confirmer votre compte. Si le message contient aussi un code, vous pouvez le saisir ici.",
        );
      } else
        setError(
          "La connexion n’est pas confirmée. Réessayez avec votre compte.",
        );
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <dialog ref={dialog} onCancel={onClose} aria-labelledby="auth-title">
      <button
        className="icon-button dialog-close"
        aria-label="Fermer"
        onClick={onClose}
      >
        <Icon name="close" />
      </button>
      <span className="eyebrow">Bienvenue dans le réseau</span>
      <h2 id="auth-title">
        {awaitingConfirmation
          ? "Confirmez votre adresse e-mail."
          : mode === "signup"
            ? "Trouvons nos points communs."
            : "Heureux de vous retrouver."}
      </h2>
      <p className="muted">
        {awaitingConfirmation
          ? confirmationMessage
          : mode === "signup"
            ? "Un compte gratuit pour exprimer un besoin, proposer votre aide et créer des liens."
            : "Votre profil et vos demandes vous attendent."}
      </p>
      <form onSubmit={submit} className="form-stack">
        {!awaitingConfirmation && mode === "signup" && (
          <label>
            Votre prénom ou nom d’usage
            <input
              required
              autoComplete="name"
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
        )}
        <label>
          Adresse e-mail
          <input
            required
            type="email"
            autoComplete="email"
            readOnly={awaitingConfirmation}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        {awaitingConfirmation ? (
          <label>
            Code de confirmation (si présent dans le message)
            <input
              required
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={128}
            />
            <span className="field-help">Le message peut contenir uniquement un lien de confirmation.</span>
          </label>
        ) : (
          <>
            <label>
              Mot de passe
              <input
                required
                type="password"
                minLength={mode === "signup" ? 12 : 1}
                autoComplete={
                  mode === "signup" ? "new-password" : "current-password"
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {mode === "signup" && (
                <span className="field-help">Au moins 12 caractères.</span>
              )}
            </label>
          </>
        )}
        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        <button className="button primary full" disabled={busy}>
          {busy
            ? "Un instant…"
            : awaitingConfirmation
              ? "Confirmer mon adresse"
              : mode === "signup"
                ? "Créer mon compte gratuit"
                : "Me connecter"}
          <Icon name="arrow" />
        </button>
      </form>
      <button
        className="text-button full"
        onClick={() => {
          setMode(
            awaitingConfirmation
              ? "login"
              : mode === "signup"
                ? "login"
                : "signup",
          );
          setAwaitingConfirmation(false);
          setConfirmationCode("");
          setConfirmationMessage("");
          setError("");
        }}
      >
        {awaitingConfirmation
          ? "Revenir à la connexion"
          : mode === "signup"
            ? "J’ai déjà un compte"
            : "Créer un compte gratuit"}
      </button>
      <p className="fineprint">
        Votre profil est privé par défaut. Vous choisissez ce que les autres
        membres peuvent découvrir.
      </p>
    </dialog>
  );
}

export function ProfileScreen({
  profile,
  catalog,
  onSave,
  onLogout,
  onRequests,
  onNotification,
}: {
  profile: Profile;
  catalog: Catalog;
  onSave: () => Promise<void>;
  onLogout: () => void;
  onRequests: () => void;
  onNotification?: (notification: import("./api").Notification) => void;
}) {
  const [section, setSection] = useState<
    "profile" | "preferences" | "notifications"
  >("profile");
  const [name, setName] = useState(profile.display_name);
  const [bio, setBio] = useState(profile.bio ?? "");
  const [skills, setSkills] = useState(
    (profile.offered_skills ?? []).join(", "),
  );
  const [visibility, setVisibility] = useState<Visibility>(profile.visibility);
  const [remote, setRemote] = useState(profile.remote);
  const [places, setPlaces] = useState(profile.place_ids ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      await api.saveProfile({
        display_name: name,
        bio,
        offered_skills: skills
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        visibility,
        remote,
        place_ids: places,
      });
      await onSave();
      setSaved(true);
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <PageTitle eyebrow="Vous, dans le réseau" title="Mon profil">
        À vous de décider comment vous souhaitez être découvert.
      </PageTitle>
      <div className="segments segments-three" aria-label="Vues du profil">
        <button
          aria-pressed={section === "profile"}
          onClick={() => setSection("profile")}
        >
          Présentation
        </button>
        <button
          aria-pressed={section === "preferences"}
          onClick={() => setSection("preferences")}
        >
          Préférences
        </button>
        <button
          aria-pressed={section === "notifications"}
          onClick={() => setSection("notifications")}
        >
          Notifications
        </button>
      </div>
      {section === "preferences" ? (
        <PreferencesPanel />
      ) : section === "notifications" ? (
        <NotificationsPanel onRequests={onRequests} onNotification={onNotification} />
      ) : (
        <>
          <div className="profile-summary">
            <span className="avatar large">
              {(name || "A").slice(0, 1).toUpperCase()}
            </span>
            <div>
              <h2>{name || "Votre profil"}</h2>
              <Badge>
                {visibility === "members"
                  ? "Visible dans la Guilde"
                  : "Profil privé"}
              </Badge>
            </div>
          </div>
          <form
            className="panel form-stack"
            onSubmit={submit}
            onChange={() => setSaved(false)}
          >
            <label>
              Nom d’usage
              <input
                required
                value={name}
                maxLength={80}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />
            </label>
            <label>
              En quelques mots
              <textarea
                value={bio}
                maxLength={500}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Ce qui vous anime, ce que vous aimez construire…"
              />
            </label>
            <label>
              Ce que je peux apporter
              <input
                value={skills}
                maxLength={1000}
                onChange={(e) => setSkills(e.target.value)}
                placeholder="Design, jardinage, photographie…"
              />
              <span className="field-help">
                Séparez vos compétences par une virgule. Elles décrivent ce que
                vous proposez, pas ce que vous recherchez.
              </span>
            </label>
            <label>
              Qui peut découvrir mon profil ?
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as Visibility)}
              >
                <option value="private">Moi uniquement</option>
                <option value="members">Les membres connectés</option>
              </select>
            </label>
            <p className="note">
              <Icon name="shield" />
              {visibility === "private"
                ? "Votre profil n’apparaît pas dans les suggestions d’Ava aux autres membres."
                : "Votre nom, votre présentation et vos compétences peuvent être proposés aux membres dont le besoin correspond."}
            </p>
            <label className="check-label">
              <input
                type="checkbox"
                checked={remote}
                onChange={(e) => setRemote(e.target.checked)}
              />
              Je peux aussi contribuer à distance
            </label>
            <fieldset>
              <legend>Mes lieux de rencontre</legend>
              {catalog.places.map((place) => (
                <label className="check-label" key={place.id}>
                  <input
                    type="checkbox"
                    checked={places.includes(place.id)}
                    onChange={(e) =>
                      setPlaces(
                        e.target.checked
                          ? [...places, place.id]
                          : places.filter((id) => id !== place.id),
                      )
                    }
                  />
                  {place.name}
                </label>
              ))}
            </fieldset>
            {error && (
              <p role="alert" className="error">
                {error}
              </p>
            )}
            {saved && (
              <p role="status" className="success">
                Votre profil est enregistré.
              </p>
            )}
            <button className="button primary" disabled={busy}>
              {busy ? "Enregistrement…" : "Enregistrer mon profil"}
              <Icon name="check" />
            </button>
          </form>
        </>
      )}
      <div className="section-heading">
        <span className="muted">Compte Avalon</span>
        <button className="text-button" onClick={onLogout}>
          Me déconnecter
        </button>
      </div>
      <p className="fineprint">
        Votre participation au réseau ne vaut pas accord de prospection
        commerciale.
      </p>
    </>
  );
}
